"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useAnalytics, getEvent } from "@/lib/analytics";
import { useAnnouncementStore } from "../model/use-announcement-store";
import { FeedbackForm } from "./feedback-form";
import { FeedbackPanelAutoOpenContext, FeedbackPanelExpansionContext, FeedbackPanelHeaderContext } from "./feedback-panel-context";
import { FeedbackTabSwitch } from "./feedback-tab-switch";

// ============================================================================
// Component
// ============================================================================

interface FeedbackSidebarProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly featureKey?: string | null;
  readonly yourFeedbackContent?: React.ReactNode;
  /** Unread replies awaiting the user — same count the Feedback button badges. */
  readonly feedbackUnreadCount?: number;
}
export function FeedbackSidebar(t0) {
  const $ = _c(30);
  const {
    isOpen,
    onClose,
    featureKey,
    yourFeedbackContent,
    feedbackUnreadCount: t1
  } = t0;
  const feedbackUnreadCount = t1 === undefined ? 0 : t1;
  const {
    track
  } = useAnalytics();
  const [tab, setTab] = useState("give");
  const [panelWantsExpand, setPanelWantsExpand] = useState(false);
  const expanded = tab === "yours" && panelWantsExpand;
  const [panelHeader, setPanelHeader] = useState(null);
  const headerSlot = tab === "yours" ? panelHeader : null;
  const [yoursEverOpened, setYoursEverOpened] = useState(false);
  if (tab === "yours" && !yoursEverOpened) {
    setYoursEverOpened(true);
  }
  const [autoOpen, setAutoOpen] = useState(false);
  const isCapturingScreen = useAnnouncementStore(_temp);
  const isChatHistoryOpen = useAnnouncementStore(_temp2);
  const sidebarRef = useRef(null);
  const [openedOverHistory, setOpenedOverHistory] = useState(false);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      const hasUnread = feedbackUnreadCount > 0;
      setTab(hasUnread ? "yours" : "give");
      setAutoOpen(hasUnread);
      if (isChatHistoryOpen) {
        setOpenedOverHistory(true);
      }
    } else {
      setAutoOpen(false);
      setOpenedOverHistory(false);
    }
  }
  if (!isChatHistoryOpen && openedOverHistory) {
    setOpenedOverHistory(false);
  }
  const shouldHideForHistory = isChatHistoryOpen && !openedOverHistory;
  let t2;
  if ($[0] !== featureKey || $[1] !== track) {
    t2 = () => {
      track(getEvent("feature-announcement", "feedback_sidebar_opened"), {
        featureKey: featureKey ?? null,
        displayMode: "sidebar"
      });
    };
    $[0] = featureKey;
    $[1] = track;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const trackOpened = useEffectEvent(t2);
  let t3;
  if ($[3] !== isOpen || $[4] !== trackOpened) {
    t3 = () => {
      if (isOpen) {
        trackOpened();
      }
    };
    $[3] = isOpen;
    $[4] = trackOpened;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isOpen) {
    t4 = [isOpen];
    $[6] = isOpen;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[8] !== isOpen) {
    t5 = () => {
      if (!isOpen) {
        return;
      }
      const mq = window.matchMedia("(max-width: 480px)");
      if (!mq.matches) {
        return;
      }
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    };
    t6 = [isOpen];
    $[8] = isOpen;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[11] !== isOpen || $[12] !== onClose) {
    t7 = () => {
      if (!isOpen) {
        return;
      }
      const handleKey = function handleKey(e) {
        if (e.key === "Escape") {
          onClose();
        }
      };
      window.addEventListener("keydown", handleKey);
      return () => window.removeEventListener("keydown", handleKey);
    };
    t8 = [isOpen, onClose];
    $[11] = isOpen;
    $[12] = onClose;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] !== autoOpen || $[16] !== expanded || $[17] !== featureKey || $[18] !== feedbackUnreadCount || $[19] !== headerSlot || $[20] !== isCapturingScreen || $[21] !== isOpen || $[22] !== onClose || $[23] !== shouldHideForHistory || $[24] !== tab || $[25] !== yourFeedbackContent || $[26] !== yoursEverOpened) {
    t9 = isOpen && <><motion.div className="fixed inset-0 z-1499 bg-black/20 max-[480px]:block hidden" style={{
        visibility: isCapturingScreen ? "hidden" : undefined
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} aria-hidden="true" /><motion.aside ref={sidebarRef} role="complementary" aria-label="Send feedback" className={cn("fixed right-0 top-0 z-1500 flex h-screen max-[480px]:w-full flex-col bg-white shadow-2xl transition-[width] duration-200 ease-out", expanded ? "w-[780px]" : "w-[400px]")} style={{
        visibility: isCapturingScreen ? "hidden" : undefined,
        pointerEvents: shouldHideForHistory ? "none" : undefined
      }} initial={{
        x: "100%"
      }} animate={{
        x: shouldHideForHistory ? "100%" : 0
      }} exit={{
        x: "100%"
      }} transition={{
        type: "spring",
        damping: 30,
        stiffness: 300
      }}><div className="flex items-center gap-3 border-b border-gray-100 px-6 py-4"><div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#6366f1]/10"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg></div><h2 className={cn("text-base font-semibold text-gray-900", headerSlot && "max-[480px]:hidden")}>Feedback</h2>{headerSlot && <div className="flex min-w-0 flex-1 items-center gap-2">{headerSlot}</div>}<button type="button" onClick={onClose} aria-label="Close feedback" className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></button></div>{yourFeedbackContent && <FeedbackTabSwitch tab={tab} onChange={setTab} unreadCount={feedbackUnreadCount} className="px-4" />}<div className={cn(yourFeedbackContent && tab === "yours" ? "hidden" : "contents")}><FeedbackForm featureKey={featureKey} variant="sidebar" isOpen={isOpen} onSuccess={onClose} /></div>{yourFeedbackContent && yoursEverOpened && <FeedbackPanelExpansionContext.Provider value={setPanelWantsExpand}><FeedbackPanelHeaderContext.Provider value={setPanelHeader}><FeedbackPanelAutoOpenContext.Provider value={autoOpen}><div className={cn("min-h-0 overflow-hidden", tab === "yours" ? "flex-1" : "hidden")}>{yourFeedbackContent}</div></FeedbackPanelAutoOpenContext.Provider></FeedbackPanelHeaderContext.Provider></FeedbackPanelExpansionContext.Provider>}</motion.aside></>;
    $[15] = autoOpen;
    $[16] = expanded;
    $[17] = featureKey;
    $[18] = feedbackUnreadCount;
    $[19] = headerSlot;
    $[20] = isCapturingScreen;
    $[21] = isOpen;
    $[22] = onClose;
    $[23] = shouldHideForHistory;
    $[24] = tab;
    $[25] = yourFeedbackContent;
    $[26] = yoursEverOpened;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t9) {
    t10 = <AnimatePresence>{t9}</AnimatePresence>;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}
function _temp2(s_0) {
  return s_0.isChatHistoryOpen;
}
function _temp(s) {
  return s.isCapturingScreen;
}